import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { RootState } from '../';
import { AzureMonitoringService } from '../../services/AzureMonitoringService';
import { AbstractEffects } from '../AbstractEffects';
import * as actions from './stock-count.actions';
import {
  StockCountsCreateAction,
  StockCountsDetailLoadAction,
  StockCountsMaterialListLoadAction,
  StockCountsOrderListLoadAction,
  StockCountsPrintLoadAction,
  StockCountsShipToLoadAction,
} from './stock-count.actions';
import { StockCountRestService } from 'app/core/services/rest/stock-count.rest.service';
import { StockCountCreated, StockCountCreateOrderList, StockCountDetail, StockCountItem, StockCountList } from './stock-count.interface';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { StockCountModalComponent } from 'app/modules/stock-count/stock-count-modal/stock-count-modal.component';
import { Definition } from '../definitions/definition.interface';
import { StockCountSandbox } from 'app/core/sandboxes/stock-count.sandbox';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';
import { util } from 'app/core/util/util';

@Injectable({ providedIn: 'root' })
export class StockCountsEffects extends AbstractEffects {
  sandbox: StockCountSandbox;
  loadStockCounts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.STOCK_COUNTS_FILTER_VALUES_LOAD),
      withLatestFrom(this.store, (action, state) => {
        const filter = state.stockCount.filter;
        return { filter };
      }),
      switchMap(({ filter }) => {
        return this.stockCountRestService.getStockCounts(filter).pipe(
          map((data: StockCountList) => {
            return new actions.StockCountsFilterValuesLoadSuccessAction(data);
          }),
          catchError(err => {
            return this.handleFail(err, 'Get stockcount list', [of(new actions.StockCountsFilterValuesLoadFailAction(err))]);
          })
        );
      })
    )
  );

  loadStockCountDetail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<StockCountsDetailLoadAction>(actions.STOCK_COUNTS_DETAIL_LOAD),
      switchMap(({ payload }) => {
        return this.stockCountRestService.getStockCountDetail(payload).pipe(
          map((data: StockCountDetail) => {
            return new actions.StockCountsDetailLoadSuccessAction(data);
          }),
          catchError(err => {
            return this.handleFail(err, 'Get stockcount detail', [of(new actions.StockCountsDetailLoadFailAction(err))]);
          })
        );
      })
    )
  );

  loadStockCountOrders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<StockCountsOrderListLoadAction>(actions.STOCK_COUNTS_ORDER_LIST_LOAD),
      switchMap(({ payload }) => {
        return this.stockCountRestService.getStockCountOrderList(parseInt(payload, 10)).pipe(
          map((data: StockCountCreateOrderList) => {
            return new actions.StockCountsOrderListLoadSuccessAction(data);
          }),
          catchError(err => {
            return this.handleFail(err, 'Get stockcount order list', [of(new actions.StockCountsOrderListLoadFailAction(err))]);
          })
        );
      })
    )
  );

  loadStockCountMaterials$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<StockCountsMaterialListLoadAction>(actions.STOCK_COUNTS_MATERIAL_LIST_LOAD),
      switchMap(({ payload }) => {
        return this.stockCountRestService.getStockCountMaterialList(payload).pipe(
          map((data: { materials: { packingID: string; amount: string }[] }) => {
            return new actions.StockCountsMaterialListLoadSuccessAction(
              data.materials.map(m => {
                let amount;
                if (m.amount) {
                  const match = m.amount && /^(\d+)\-$/.exec(m.amount);
                  if (match) {
                    amount = -1 * parseInt(match[1], 10);
                  } else {
                    amount = parseInt(m.amount, 10);
                  }
                }
                return {
                  amount,
                  packingID: m.packingID,
                };
              })
            );
          }),
          catchError(err => {
            return this.handleFail(err, 'Get stockcount material list', [of(new actions.StockCountsMaterialListLoadFailAction(err))]);
          })
        );
      })
    )
  );

  createStockCount$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<StockCountsCreateAction>(actions.STOCK_COUNTS_CREATE),
      withLatestFrom(this.store, (action, state) => {
        return { action, state };
      }),
      switchMap(({ action, state }: { action: StockCountsCreateAction; state: RootState }) => {
        const firstName = state.session.userContext.firstName;
        return this.stockCountRestService.createStockCount(action.payload).pipe(
          map((data: StockCountCreated) => {
            this.openDialog(data.stockCountNo, firstName);
            return new actions.StockCountsCreateSuccessAction(data);
          }),
          catchError(err => {
            return this.handleFail(err, 'Create stock count', [of(new actions.StockCountsCreateFailAction(err))]);
          })
        );
      })
    )
  );

  loadShipTos$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<StockCountsShipToLoadAction>(actions.STOCK_COUNTS_SHIPTO_LOAD),
      withLatestFrom(this.store, (action, state) => {
        return { action, state };
      }),
      switchMap(({ action, state }: { action: StockCountsShipToLoadAction; state: RootState }) => {
        return this.stockCountRestService.getShipTos(action.payload).pipe(
          map((data: any) => {
            const mappedShipTo: Definition[] = [];
            const receivedShipTos = data && data.items && data.items[0] && data.items[0].shipTo;
            const gotStateShipTos = state && state.customerInfo && state.customerInfo.customer.shipTos;
            if (receivedShipTos && gotStateShipTos) {
              // map shiptos to
              data.items[0].shipTo.map(shipToId => {
                state.customerInfo.customer.shipTos.map(s => {
                  if (s.id === shipToId) {
                    mappedShipTo.push(s);
                  }
                });
              });
            }
            return new actions.StockCountsShipToSuccessAction(mappedShipTo);
          }),
          catchError(err => {
            return this.handleFail(err, 'Shipto stock count', [of(new actions.StockCountsShipToFailAction(err))]);
          })
        );
      })
    )
  );

  printStockCount$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<StockCountsPrintLoadAction>(actions.STOCK_COUNTS_PRINT_LOAD),
      switchMap(({ payload }) => {
        return this.stockCountRestService.printStockCount(payload).pipe(
          map((blob: Blob) => {
            return new actions.StockCountsPrintSuccessAction({
              filename: `stockCount.${util.getExtension(blob.type)}`,
              blob,
            });
          }),
          catchError(err => {
            return this.handleFail(err, 'Get printed stock count', [of(new actions.StockCountsPrintFailAction(err))]);
          })
        );
      })
    )
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    public dialog: MatDialog,
    private actions$: Actions,
    private stockCountRestService: StockCountRestService,
    translateService: TranslateService,
    sandbox: StockCountSandbox,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
    this.sandbox = sandbox;
  }

  private openDialog(stockCountNo: string, name: string) {
    const config: MatDialogConfig = {
      width: '75%',
      height: 'auto',
      role: 'dialog',
      disableClose: true,
      data: {
        name,
        number: stockCountNo,
        type: 'success',
      },
    };

    const dialogRef = this.dialog.open(StockCountModalComponent, config);
    dialogRef.afterClosed().subscribe(result => {
      if (result.goToOverview) {
        // go to overview
        this.sandbox.cancelCreate();
      } else if (result.goToDetail) {
        const stockCountItem: StockCountItem = {
          stockCountDate: '',
          stockCountNo: result.number,
          shipTo: '',
        };
        if (stockCountItem.stockCountNo) {
          this.sandbox.openStockCount(stockCountItem);
        } else {
          this.sandbox.cancelCreate();
        }
      } else if (result.goToCreate) {
        this.sandbox.resetCreate();
      }
    });
  }
}
